import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BadgeCheck, ChevronLeft, ChevronRight, MessageCircle, MapPin, ShieldCheck, Loader2 } from "lucide-react";

/**
 * Public "Book me" style page at /availability/:id
 * Shows only anonymous busy/free blocks — no titles, no personal info.
 * Free slot → visitor clicks → sent to /messages/:id (login required).
 */
export default function PublicAvailability() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, err: "", user: null, busy: [] });
  const [cursor, setCursor] = useState(startOfMonth(new Date()));

  const from = new Date(cursor.getFullYear(), cursor.getMonth(), 1).toISOString();
  const to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59).toISOString();

  useEffect(() => {
    let alive = true;
    setState(s => ({ ...s, loading: true, err: "" }));
    api.get(`/availability/${id}?from=${from}&to=${to}`)
      .then(r => alive && setState({ loading: false, err: "", user: r.data.user, busy: r.data.busy || [] }))
      .catch(e => alive && setState({ loading: false, err: e?.response?.data?.detail || "Unable to load availability", user: null, busy: [] }));
    return () => { alive = false; };
  }, [id, from, to]);

  const busyByDay = useMemo(() => {
    const map = new Map();
    (state.busy || []).forEach(b => {
      if (!b.start) return;
      const d = new Date(b.start);
      const k = ymd(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(b);
    });
    return map;
  }, [state.busy]);

  if (state.loading) return <div className="p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline-block"/> Loading…</div>;
  if (state.err)     return (
    <div className="p-16 text-center max-w-md mx-auto">
      <p className="text-[#B84B4B] font-medium">{state.err}</p>
      <Link to="/" className="btn-secondary inline-flex mt-6">Back to Hoomanz</Link>
    </div>
  );

  const u = state.user;
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const days = buildMonthGrid(cursor);
  const todayKey = ymd(new Date());

  const bookSlot = (day) => {
    if (!user) { nav("/login"); return; }
    if (user.id === id) { nav("/dashboard"); return; }
    nav(`/messages/${id}?about=availability&date=${ymd(day)}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="availability-page">
      {/* Header */}
      <div className="card p-6 flex items-center gap-4" data-testid="availability-header">
        {u?.avatar && <img src={u.avatar} alt="" className="h-16 w-16 rounded-full object-cover bg-[#E5E5E3]"/>}
        <div className="flex-1 min-w-0">
          <p className="overline">Book me · Availability</p>
          <h1 className="text-2xl font-heading mt-0.5 inline-flex items-center gap-1.5">
            {u?.name} {u?.verified && <BadgeCheck size={16} className="text-[#0057FF]"/>}
          </h1>
          <p className="text-xs text-[#8A8A8A] mt-1">
            {u?.category}
            {u?.location && <> · <MapPin size={10} className="inline mr-0.5"/> {u.location}</>}
          </p>
        </div>
        <Link to={`/u/${id}`} className="btn-ghost text-xs" data-testid="availability-profile-link">View profile</Link>
      </div>

      {u?.verified && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0057FF]" data-testid="availability-verified-badge">
          <ShieldCheck size={13}/> Verified Human on Hoomanz
        </div>
      )}

      {/* Calendar */}
      <div className="mt-5 card p-4 sm:p-5" data-testid="availability-calendar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-[#F2F2F0]"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                    aria-label="Previous month">
              <ChevronLeft size={16}/>
            </button>
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-[#F2F2F0]"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                    aria-label="Next month">
              <ChevronRight size={16}/>
            </button>
          </div>
          <p className="font-medium text-lg">{monthLabel}</p>
          <div className="flex items-center gap-3 text-[10px] text-[#8A8A8A]">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#B84B4B]"/> Busy</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#0A7A5C]"/> Free</span>
          </div>
        </div>

        <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#8A8A8A] mb-1">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} className="px-1 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const key = ymd(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const past = d < new Date(new Date().setHours(0,0,0,0));
            const isBusy = (busyByDay.get(key) || []).length > 0;
            const disabled = past || !inMonth;
            const label = past ? "Past date" : isBusy ? "Busy — tap to ask about another day" : "Free — tap to request this day";
            return (
              <button key={i} disabled={disabled}
                onClick={() => bookSlot(d)}
                aria-label={`${key} · ${label}`}
                data-testid={`avail-day-${key}`}
                className={`min-h-[64px] rounded-xl border p-2 flex flex-col items-start gap-1 text-xs transition-all
                  ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-[#0A0A0A]"}
                  ${key === todayKey ? "border-[#0A0A0A]" : "border-[#E5E5E3]"}
                  ${inMonth ? "bg-white" : "bg-[#FAFAF8]"}
                `}>
                <span className={`text-[11px] font-medium ${key === todayKey ? "bg-[#0A0A0A] text-white rounded-full px-1.5" : ""}`}>{d.getDate()}</span>
                {inMonth && !past && (
                  isBusy
                    ? <span className="text-[10px] text-[#B84B4B] font-medium">Busy</span>
                    : <span className="text-[10px] text-[#0A7A5C] font-medium">Free</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-[#8A8A8A] mt-4">
          Tap a free day to message {u?.name?.split(" ")[0] || "them"} and request a time. Personal titles are never shown — you only see busy vs. free.
        </p>
      </div>

      <div className="mt-4 flex items-center justify-center">
        <Link to={`/messages/${id}`} className="btn-primary" data-testid="availability-message-btn">
          <MessageCircle size={14}/> Message {u?.name?.split(" ")[0] || "them"}
        </Link>
      </div>
    </div>
  );
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() - startWeekday + i);
    days.push(d);
  }
  return days;
}
